import { Button, HStack, Text, VStack } from '@chakra-ui/react';
import { Input } from '@gamma/ui/form-fields';
import { Alert } from '@gamma/ui/overlay';
import { formatOTPCode } from '@gamma/ui/util';
import { Auth } from 'aws-amplify';
import { ChangeEvent, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { i18n } from '../../../../../shared/localization';
import { CognitoUserInterface } from '../../../../../shared/types/auth-types';

import { IHelperText } from '../types';

interface MfaHintInterface {
  hint: string;
  error: string;
}

interface AuthMFABodyProps {
  isButtonLoading: boolean;
  width?: string;
  helperTexts: IHelperText;
  verifyUser: CognitoUserInterface;
  isExportNoMfa?: boolean;
  setIsButtonLoading: (e: boolean) => void;
  setIsViewInfoButtonDisabled: (e: boolean) => void;
}

interface MfaForm {
  OTPCode: string;
}

export const AuthMFABody = ({
  isButtonLoading,
  width = '86%',
  helperTexts,
  isExportNoMfa,
  setIsButtonLoading,
  setIsViewInfoButtonDisabled,
  verifyUser,
}: AuthMFABodyProps) => {
  const { mfaValid, mfaInvalid } = i18n.forms.validation;
  const [mfaHint, setMfaHint] = useState<MfaHintInterface>({
    hint: '',
    error: '',
  });
  const { register, handleSubmit, setFocus, setValue } = useForm<MfaForm>();
  const { warning } = helperTexts.mfa;

  useEffect(() => {
    setFocus('OTPCode');
  }, []);

  const handleOTPInputChange = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => {
    setValue('OTPCode', formatOTPCode(value));
  };
  const handleMFASubmit: SubmitHandler<{ OTPCode: string }> = async ({
    OTPCode,
  }) => {
    setIsButtonLoading(true);
    try {
      const verifiedUser = await Auth.confirmSignIn(
        verifyUser,
        OTPCode.replace(' ', ''),
        'SOFTWARE_TOKEN_MFA',
      );

      if (verifiedUser) {
        const user = await Auth.currentAuthenticatedUser();
        if (user) {
          setMfaHint({ hint: mfaValid, error: '' });
          setIsViewInfoButtonDisabled(false);
        }
      }
    } catch (error: unknown) {
      console.error(error);
      setMfaHint({ hint: '', error: mfaInvalid });
    }

    setIsButtonLoading(false);
  };

  return (
    <VStack spacing={4}>
      {warning && (
        <Alert
          status="warning"
          title={warning}
          data-testid="auth-mfa-warning-alert"
        />
      )}
      {!isExportNoMfa && (
        <>
          <Text alignSelf="flex-start" data-testid="auth-mfa-helper-text">
            {helperTexts.mfa.text}
          </Text>

          <HStack
            w={width}
            p={4}
            borderRadius="4px"
            as="form"
            onSubmit={handleSubmit(handleMFASubmit)}
          >
            <Input
              {...register('OTPCode', {
                onChange: handleOTPInputChange,
              })}
              data-testid="auth-modal-otp-input"
              label={i18n.forms.labels.enterOTP}
              hint={mfaHint.hint}
              error={mfaHint.error}
              placeholder="xxx xxx"
              isLabelHidden
            />
            <Button
              alignSelf="flex-start"
              data-testid="auth-modal-otp-verify"
              variant="solid"
              isLoading={isButtonLoading}
              type="submit"
            >
              {i18n.buttons.verify}
            </Button>
          </HStack>
        </>
      )}
    </VStack>
  );
};
